import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEND_INTERVAL_MS = 30000;

// Deelt de locatie van de chauffeur elke 30 sec zolang de rit actief is en de pagina openstaat.
export const useDriverTracking = (token, pin) => {
  const flagKey = `driver_tracking_${token}`;
  const [active, setActive] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [error, setError] = useState('');
  const [lastPos, setLastPos] = useState(null);
  const positionRef = useRef(null);
  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const lastSentRef = useRef(0);
  const wakeRef = useRef(null);

  const headers = { 'X-Driver-Pin': pin };

  const send = useCallback(async (force = false) => {
    const p = positionRef.current;
    if (!p || (!force && Date.now() - lastSentRef.current < SEND_INTERVAL_MS - 1000)) return;
    lastSentRef.current = Date.now();
    try {
      await axios.post(`${API}/driver/${token}/location`, p, { headers: { 'X-Driver-Pin': pin } });
      setLastSent(new Date());
    } catch (e) { /* volgende poging over 30 sec */ }
  }, [token, pin]);

  const requestWakeLock = async () => {
    try { wakeRef.current = await navigator.wakeLock?.request('screen'); } catch (e) { /* niet ondersteund */ }
  };

  const cleanup = () => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    clearInterval(timerRef.current);
    watchRef.current = null;
    wakeRef.current?.release?.().catch(() => {});
    wakeRef.current = null;
  };

  const start = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Deze telefoon ondersteunt geen locatie delen');
      return;
    }
    setError('');
    cleanup();
    let first = true;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        positionRef.current = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setLastPos(positionRef.current);
        send(first);
        first = false;
      },
      (err) => {
        setError(err.code === 1
          ? 'Locatie geweigerd. Sta locatie toe voor deze website in je browserinstellingen.'
          : 'Locatie tijdelijk niet beschikbaar');
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
    );
    timerRef.current = setInterval(() => send(true), SEND_INTERVAL_MS);
    requestWakeLock();
    localStorage.setItem(flagKey, '1');
    setActive(true);
    axios.post(`${API}/driver/${token}/tracking`, { active: true }, { headers }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [send, token, pin, flagKey]);

  const stop = useCallback(() => {
    cleanup();
    localStorage.removeItem(flagKey);
    setActive(false);
    axios.post(`${API}/driver/${token}/tracking`, { active: false }, { headers }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pin, flagKey]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && watchRef.current !== null) { requestWakeLock(); send(true); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wasActive = localStorage.getItem(flagKey) === '1';
  return { active, start, stop, lastSent, error, positionRef, wasActive, lastPos };
};
